/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, February 11th 2022, 5:53:33 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { useDrop } from "react-dnd";
import { Identifier } from "dnd-core";

export interface UseGenericDropTargetSpec<GenericDraggedItemState> {
  itemType: Identifier;
  handleDrop: (item: GenericDraggedItemState) => void; // Callback for when the dragged item is dropped to a valid drop target
}

interface GenericCollectedDropSpec {
  targetIsBeingDraggedOver: boolean;
}

export const useGenericDropTarget = <GenericDraggedItemState,>({
  itemType,
  handleDrop,
}: UseGenericDropTargetSpec<GenericDraggedItemState>) => {
  const [{ targetIsBeingDraggedOver }, dropTargetRef] = useDrop<
    GenericDraggedItemState,
    void,
    GenericCollectedDropSpec
  >(
    () => ({
      accept: itemType,
      drop: (item, monitor) => {
        handleDrop(item);
      },
      collect: (monitor) => ({
        targetIsBeingDraggedOver: !!monitor.isOver(),
      }),
    }),
    [handleDrop]
  );

  return { dropTargetRef, targetIsBeingDraggedOver };
};

interface GenericDropTargetProps<GenericDraggedItemState> extends UseGenericDropTargetSpec<GenericDraggedItemState> {
  children: React.ReactNode;
}

export const GenericDropTarget = <GenericDraggedItemState,>({
  children,
  itemType,
  handleDrop,
}: GenericDropTargetProps<GenericDraggedItemState>) => {
  const { dropTargetRef } = useGenericDropTarget<GenericDraggedItemState>({
    itemType,
    handleDrop,
  });

  return <div ref={dropTargetRef}>{children}</div>;
};
